import { useId } from 'react';
import type { ReactNode } from 'react';

import { BackButton } from '../../components/BackButton';
import { Button } from '../../components/Button';
import { LockedSection } from '../../components/LockedSection';
import { PageHeading } from '../../components/PageHeading';
import { BillingDetailsForm } from '../../composites/BillingDetailsForm';
import { CheckoutSummaryMobile } from '../../composites/CheckoutSummaryMobile';
import { CheckoutSummaryPanel } from '../../composites/CheckoutSummaryPanel';
import { DeliveryStep } from '../../composites/DeliveryStep';
import { DepositPaymentPicker } from '../../composites/DepositPaymentPicker';
import { DiscountCodeDisclosure } from '../../composites/DiscountCodeDisclosure';
import { PaymentMethodForm } from '../../composites/PaymentMethodForm';
import { SignInModal } from '../../composites/SignInModal';
import {
  BackButtonSlot,
  ExpressCheckoutBlock,
  ExpressCheckoutDivider,
  ExpressCheckoutLabel,
  HeadingRow,
  LockedHint,
  MainColumn,
  MobileOnly,
  PageGrid,
  PageWrapper,
  SidebarColumn,
  StepBlock,
  StepHeaderRow,
  StepNumber,
  StepTitle,
  SubmitButtonWrapper,
  SubmittingOverlay,
  TermsLine,
  TermsLink,
} from './CheckoutTemplate.styles';
import type { CheckoutTemplateProps } from './CheckoutTemplate.types';

const DEFAULT_PROTECTION_TITLE = 'Refundable booking';
const DEFAULT_PAYMENT_LOCKED_HINT = 'Choose an option in step 1 to continue.';

type LegalLinkProps = {
  href?: string;
  onClick?: () => void;
  children: ReactNode;
};

function LegalLink({ href, onClick, children }: LegalLinkProps) {
  if (!href) return <>{children}</>;
  return (
    <TermsLink href={href} onClick={onClick} target="_blank" rel="noreferrer">
      {children}
    </TermsLink>
  );
}

export function CheckoutTemplate({
  requiresPayment,
  isSubmitting = false,
  submitDisabled = false,
  isLoggedIn = false,

  protectionSlot,
  paymentLocked = false,

  depositOptions,
  depositValue,
  onDepositChange,

  billingValues,
  onBillingChange,
  billingErrors,
  countryOptions,
  phoneCountryCodes,
  minimalPersonalDetails = false,

  hasDeliverableItems = false,
  isPostalAllowed = true,
  isCollectionAllowed = true,
  deliveryCountryOptions,
  deliveryValues,
  onDeliveryMethodChange,
  postalCost,
  collectionDetails,

  paymentSlot,
  acceptedCards,
  paymentError,
  paymentLabelsOverride,

  expressCheckoutSlot,
  deliveryAddressSlot,
  billingAddressSlot,

  onSubmit,
  onBack,

  signInOpen,
  onSignInOpenChange,
  signInValues,
  onSignInChange,
  onSignInSubmit,
  signInErrors,
  isSigningIn,
  onForgottenPasswordClick,

  signUpValues,
  onSignUpChange,
  onSignUpSubmit,
  isSigningUp,
  signUpErrors,

  basketItems,
  basketTotal,
  basketView,
  onBasketViewChange,
  basketGuests,
  onBasketRemoveItem,
  basketProtectionFee,
  basketSubtotal,
  basketSpecialOffersDiscount,
  basketPromotionsDiscount,
  basketVouchersDiscount,
  basketDeliveryCost,
  basketDueToday,
  basketDueLater,
  basketDueLaterNote,
  promoCode,
  voucherCode,

  termsHref,
  onTermsClick,
  privacyHref,

  locale,
  labels,
}: CheckoutTemplateProps) {
  const showDeliverySteps = Boolean(
    hasDeliverableItems && deliveryValues && onDeliveryMethodChange
  );
  const isPostalDelivery = showDeliverySteps && deliveryValues?.method === 'post';
  const showDepositStep = depositOptions.length > 1;
  const showPaymentStep = Boolean(requiresPayment);

  const showDepositFirst = !showDeliverySteps && showDepositStep;

  const lockedHintId = useId();
  const lockedHint = paymentLocked ? labels.paymentLockedHint ?? DEFAULT_PAYMENT_LOCKED_HINT : '';
  let nextStepNumber = 1;
  const stepNumber = () => `${nextStepNumber++}.`;
  const protectionStepNumber = protectionSlot ? stepNumber() : '';
  const deliveryMethodStepNumber = showDeliverySteps ? stepNumber() : '';
  const depositStepNumberBeforeDetails = showDepositFirst ? stepNumber() : '';
  const personalDetailsStepNumber = stepNumber();
  const depositStepNumberAfterDetails = showDepositStep && !showDepositFirst ? stepNumber() : '';
  const paymentStepNumber = showPaymentStep ? stepNumber() : '';
  const depositStepNumber = showDepositFirst
    ? depositStepNumberBeforeDetails
    : depositStepNumberAfterDetails;

  const personalDetailsTitle = isPostalDelivery ? labels.deliveryDetailsTitle : labels.detailsTitle;
  const personalDetailsNumber = personalDetailsStepNumber;

  const renderBasketPanel = (withDiscountCodes: boolean) => (
    <CheckoutSummaryPanel
      items={basketItems}
      totalPrice={basketTotal}
      view={basketView}
      onViewChange={onBasketViewChange}
      guests={basketGuests}
      onRemoveItem={onBasketRemoveItem}
      protectionFee={basketProtectionFee}
      subtotal={basketSubtotal}
      specialOffersDiscount={basketSpecialOffersDiscount}
      promotionsDiscount={basketPromotionsDiscount}
      vouchersDiscount={basketVouchersDiscount}
      deliveryCost={basketDeliveryCost}
      dueToday={basketDueToday}
      dueLater={basketDueLater}
      dueLaterNote={basketDueLaterNote}
      promoCode={withDiscountCodes ? promoCode : undefined}
      voucherCode={withDiscountCodes ? voucherCode : undefined}
      locale={locale}
      labels={{
        itemisedView: labels.basketItemisedView,
        itineraryView: labels.basketItineraryView,
        addedConfirmation: labels.basketAddedConfirmation,
        ...(labels.basketProtectionFee ? { protectionFee: labels.basketProtectionFee } : {}),
        subtotal: labels.basketSubtotal,
        ...(labels.basketDiscount ? { discount: labels.basketDiscount } : {}),
        promotions: labels.basketPromotions,
        vouchers: labels.basketVouchers,
        delivery: labels.basketDelivery,
        total: labels.basketTotal,
        ...(labels.basketDueToday ? { dueToday: labels.basketDueToday } : {}),
        ...(labels.basketDueLater ? { dueLater: labels.basketDueLater } : {}),
        removeLabel: labels.basketRemoveLabel,
        noImageLabel: labels.basketNoImageLabel,
        selectGuestLabel: labels.basketSelectGuestLabel,
      }}
    />
  );

  const billingFormCountryOptions = isPostalDelivery
    ? deliveryCountryOptions ?? countryOptions
    : countryOptions;

  const signIn =
    onSignInOpenChange && onSignInSubmit && signInValues && onSignInChange
      ? {
          open: signInOpen ?? false,
          onOpenChange: onSignInOpenChange,
          values: signInValues,
          onChange: onSignInChange,
          onSubmit: onSignInSubmit,
        }
      : null;

  const personalDetailsStep = (
    <StepBlock>
      <StepHeaderRow>
        <StepTitle>
          <StepNumber>{personalDetailsNumber}</StepNumber>
          {personalDetailsTitle}
        </StepTitle>
        {signIn && !isLoggedIn && (
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => signIn.onOpenChange(true)}
          >
            {labels.signInButton}
          </Button>
        )}
      </StepHeaderRow>
      <BillingDetailsForm
        values={billingValues}
        onChange={onBillingChange}
        countryOptions={billingFormCountryOptions}
        phoneCountryCodes={phoneCountryCodes}
        showHeader={false}
        showDeliveryAddress={!minimalPersonalDetails && !deliveryAddressSlot && !billingAddressSlot}
        showNameFields={minimalPersonalDetails}
        showPhoneField={minimalPersonalDetails}
        errors={billingErrors}
        labels={{
          sectionTitle: personalDetailsTitle,
          signInButton: labels.signInButton,
          firstNameLabel: labels.billingFirstNameLabel,
          lastNameLabel: labels.billingLastNameLabel,
          emailLabel: labels.billingEmailLabel,
          marketingOptInLabel: labels.billingMarketingOptInLabel,
          deliveryAddressHeading: labels.billingDeliveryAddressHeading,
          addressLabel: labels.billingAddressLabel,
          postalCodeLabel: labels.billingPostalCodeLabel,
          countryLabel: labels.billingCountryLabel,
          countryPlaceholder: labels.billingCountryPlaceholder,
          phoneLabel: labels.billingPhoneLabel,
          phoneSearchPlaceholder: labels.billingPhoneSearchPlaceholder,
        }}
      />
      {billingAddressSlot}
      {deliveryAddressSlot}
    </StepBlock>
  );

  const depositStep = showDepositStep && (
    <LockedSection locked={paymentLocked}>
      <StepBlock>
        <StepTitle>
          <StepNumber>{depositStepNumber}</StepNumber>
          {labels.whenToPayTitle}
        </StepTitle>
        <DepositPaymentPicker
          options={depositOptions}
          value={depositValue}
          onChange={onDepositChange}
          labels={{ groupAriaLabel: labels.whenToPayTitle }}
        />
      </StepBlock>
    </LockedSection>
  );

  const expressCheckoutBlock = expressCheckoutSlot ? (
    <LockedSection locked={paymentLocked}>
      <ExpressCheckoutBlock>
        <ExpressCheckoutLabel>{labels.expressCheckoutLabel}</ExpressCheckoutLabel>
        {expressCheckoutSlot}
        <ExpressCheckoutDivider>{labels.expressCheckoutDividerText}</ExpressCheckoutDivider>
      </ExpressCheckoutBlock>
    </LockedSection>
  ) : null;

  return (
    <>
      <PageWrapper data-testid="checkout-template">
        {onBack ? (
          <HeadingRow>
            <BackButtonSlot>
              <BackButton
                variant="inline"
                onClick={onBack}
                ariaLabel={labels.backAriaLabel}
                disabled={isSubmitting}
              />
            </BackButtonSlot>
            <PageHeading title={labels.pageTitle} subtitle={labels.pageSubtitle} flush />
          </HeadingRow>
        ) : (
          <PageHeading title={labels.pageTitle} subtitle={labels.pageSubtitle} />
        )}

        <MobileOnly>
          <CheckoutSummaryMobile
            total={basketTotal}
            defaultOpen={Boolean(basketDueToday)}
            locale={locale}
            labels={{
              showSummary: labels.mobileShowSummary,
              hideSummary: labels.mobileHideSummary,
            }}
          >
            {renderBasketPanel(false)}
          </CheckoutSummaryMobile>
        </MobileOnly>

        <PageGrid>
          <MainColumn>
            <SubmittingOverlay $active={isSubmitting}>
              {protectionSlot && (
                <StepBlock>
                  <StepTitle>
                    <StepNumber>{protectionStepNumber}</StepNumber>
                    {labels.protectionTitle ?? DEFAULT_PROTECTION_TITLE}
                  </StepTitle>
                  {protectionSlot}
                </StepBlock>
              )}

              {showDeliverySteps && deliveryValues && onDeliveryMethodChange && (
                <StepBlock>
                  <StepTitle>
                    <StepNumber>{deliveryMethodStepNumber}</StepNumber>
                    {labels.deliveryTitle}
                  </StepTitle>
                  <DeliveryStep
                    values={deliveryValues}
                    onMethodChange={onDeliveryMethodChange}
                    isPostalAllowed={isPostalAllowed}
                    isCollectionAllowed={isCollectionAllowed}
                    postalCost={postalCost}
                    collectionDetails={collectionDetails}
                    labels={{
                      chooseMethod: labels.deliveryChooseMethod,
                      methodPostal: labels.deliveryMethodPostal,
                      methodCollection: labels.deliveryMethodCollection,
                    }}
                  />
                </StepBlock>
              )}

              {showDepositFirst && depositStep}

              {expressCheckoutBlock}

              {personalDetailsStep}

              {!showDepositFirst && depositStep}

              {showPaymentStep && (
                <LockedSection locked={paymentLocked}>
                  <StepBlock>
                    <StepTitle>
                      <StepNumber>{paymentStepNumber}</StepNumber>
                      {labels.paymentTitle}
                    </StepTitle>
                    <PaymentMethodForm
                      acceptedCards={acceptedCards}
                      showHeader={false}
                      formError={paymentError}
                      labels={{
                        sectionTitle: labels.paymentTitle,
                        acceptedCardsAriaLabel: labels.paymentAcceptedCardsAriaLabel,
                        ...paymentLabelsOverride,
                      }}
                    >
                      {paymentSlot}
                    </PaymentMethodForm>
                  </StepBlock>
                </LockedSection>
              )}

              {(promoCode || voucherCode) && (
                <MobileOnly>
                  <DiscountCodeDisclosure
                    promoCode={promoCode}
                    voucherCode={voucherCode}
                    labels={{ prompt: labels.discountCodesPrompt }}
                  />
                </MobileOnly>
              )}

              <SubmitButtonWrapper>
                {lockedHint && <LockedHint id={lockedHintId}>{lockedHint}</LockedHint>}
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={onSubmit}
                  disabled={isSubmitting || submitDisabled || paymentLocked}
                  aria-describedby={lockedHint ? lockedHintId : undefined}
                >
                  {requiresPayment ? labels.payButton : labels.confirmButton}
                </Button>
                <TermsLine>
                  {labels.termsPrefix}{' '}
                  <LegalLink href={termsHref} onClick={onTermsClick}>
                    {labels.termsLinkText}
                  </LegalLink>
                  {labels.privacyLinkText && (
                    <>
                      {' '}
                      {labels.termsConjunction ?? 'and'}{' '}
                      <LegalLink href={privacyHref}>{labels.privacyLinkText}</LegalLink>
                    </>
                  )}
                  {labels.termsSuffix}
                </TermsLine>
              </SubmitButtonWrapper>
            </SubmittingOverlay>
          </MainColumn>

          <SidebarColumn>{renderBasketPanel(true)}</SidebarColumn>
        </PageGrid>
      </PageWrapper>

      {signIn && (
        <SignInModal
          open={signIn.open}
          onClose={() => signIn.onOpenChange(false)}
          values={signIn.values}
          onChange={signIn.onChange}
          onSubmit={signIn.onSubmit}
          onForgottenPasswordClick={onForgottenPasswordClick}
          isSubmitting={isSigningIn}
          errors={signInErrors}
          signUpValues={signUpValues}
          onSignUpChange={onSignUpChange}
          onSignUpSubmit={onSignUpSubmit}
          isSigningUp={isSigningUp}
          signUpErrors={signUpErrors}
          labels={{
            title: labels.signInModalTitle,
            subtitle: labels.signInModalSubtitle,
            signUpPrompt: labels.signInModalSignUpPrompt,
            signUpLink: labels.signInModalSignUpLink,
            emailLabel: labels.signInModalEmailLabel,
            passwordLabel: labels.signInModalPasswordLabel,
            submit: labels.signInModalSubmit,
            forgottenPassword: labels.signInModalForgottenPassword,
            close: labels.signInModalClose,
            signUpTitle: labels.signUpModalTitle,
            signUpSubtitle: labels.signUpModalSubtitle,
            signInPrompt: labels.signUpModalSignInPrompt,
            signInLink: labels.signUpModalSignInLink,
            isSpaMember: labels.signUpModalIsSpaMember,
            spaMemberNumberLabel: labels.signUpModalSpaMemberNumberLabel,
            signUpSubmit: labels.signUpModalSubmit,
          }}
        />
      )}
    </>
  );
}
